import { FilterTree, getFieldValueFromItem } from "./SmartTableFilter";
import {
  IOptionsStorage,
  IOptionsStorageSavedFilter,
} from "../../types/ITable";

import { ISmartTableFilterLabels } from "./types";
import { ITableField } from "../../types/IFields";
import { IntegratedDialog } from "@tedivo/tedivo-ui";
import SlButton from "@shoelace-style/shoelace/dist/components/button/button.component";
import SlInput from "@shoelace-style/shoelace/dist/components/input/input.component";
import SlRadioGroup from "@shoelace-style/shoelace/dist/components/radio-group/radio-group.component";
import { assertNonNullish } from "@tedivo/tedivo-pure-helpers";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";

/* eslint-disable @typescript-eslint/no-unused-vars */
export class SmartTableFiltersMgmt<TData> {
  private _currentEditingFilterId: string | undefined = undefined;

  // HTML components
  private _dialog: IntegratedDialog<"close">;
  private _radioButtonsList: SlRadioGroup;
  private _editNameInput: SlInput | undefined = undefined;
  private _deleteButton: SlButton | undefined = undefined;
  private _saveButton: SlButton | undefined = undefined;
  private _divEdit: HTMLElement;
  private _divMiddle: HTMLElement;
  private _showConditionsHtml: HTMLElement;

  constructor(
    private labels: ISmartTableFilterLabels,
    parentNode: HTMLElement,
    private listOfSavedFilters: IOptionsStorageSavedFilter<TData>[],
    private fieldsByKey: Map<keyof TData, ITableField<TData>>,
    private userOptionsStorage:
      | undefined
      | IOptionsStorage<TData, IOptionsStorageSavedFilter<TData>>,
    private onChangeFilter: (
      listOfSavedFilters: IOptionsStorageSavedFilter<TData>[],
    ) => void,
  ) {
    assertNonNullish(userOptionsStorage, "userOptionsStorage is required");

    this._dialog = new IntegratedDialog<"close">({
      parentNode,
      width: "1100px",
      maxWidth: "90vw",
      bodyOverflow: "auto",
      removeDialogWhenHidden: true,
      buttonsAttrs: [
        {
          value: "close",
          label: this.labels.close,
          variant: "text",
          type: "submit",
        },
      ],
    });

    const holder = document.createElement("div");
    holder.className = "saved-filters-holder";

    const divListOfSavedFilters = document.createElement("div");
    divListOfSavedFilters.className = "saved-filters-list";

    const divMiddle = document.createElement("div");
    divMiddle.className = "saved-filters-middle";

    const divEdit = document.createElement("div");
    divEdit.className = "saved-filters-edit";

    const divShowConditionsHtml = document.createElement("div");
    divShowConditionsHtml.className = "saved-filters-explain";

    const radioButtonsList = document.createElement("sl-radio-group");
    this._radioButtonsList = radioButtonsList;

    listOfSavedFilters.forEach((filter) => {
      const radioButton = document.createElement("sl-radio");
      radioButton.value = filter.id;
      radioButton.innerHTML = filter.name;
      radioButtonsList.appendChild(radioButton);
    });

    divListOfSavedFilters.appendChild(radioButtonsList);

    holder.appendChild(divListOfSavedFilters);
    holder.appendChild(divMiddle);

    divMiddle.appendChild(divEdit);
    divMiddle.appendChild(divShowConditionsHtml);

    this._dialog.show(this.labels.manageFilters, holder);

    this._divEdit = divEdit;
    this._showConditionsHtml = divShowConditionsHtml;
    this._divMiddle = divMiddle;

    this.attachEvents();
  }

  get currentEditingFilterId() {
    return this._currentEditingFilterId;
  }

  set currentEditingFilterId(value: string | undefined) {
    this._currentEditingFilterId = value;
    if (value) {
      this._divMiddle.setAttribute("data-filter-id", value);
    } else {
      this._divMiddle.removeAttribute("data-filter-id");
    }
  }

  private attachEvents() {
    this._radioButtonsList.addEventListener("sl-change", (e: Event) => {
      const target = e.target as SlRadioGroup;
      const selectedFilterId = target.value;
      this.showNameEditOrDelete(selectedFilterId);
    });
  }

  private showNameEditOrDelete(selectedFilterId: string) {
    if (!selectedFilterId) return;

    const selectedFilter = this.listOfSavedFilters.find(
      (filter) => filter.id === selectedFilterId,
    );

    if (selectedFilter) {
      // Show edit name or delete button
      this.currentEditingFilterId = selectedFilterId;

      if (!this._editNameInput || !this._deleteButton) {
        this._editNameInput = document.createElement("sl-input");
        this._editNameInput.label = this.labels.editName;
        this._editNameInput.value = selectedFilter.name;
        this._editNameInput.placeholder = selectedFilter.name;
        this._editNameInput.helpText = this.labels.filterNameUnique;

        this._editNameInput.addEventListener("sl-input", this.validateNameUI);

        const iconSave = document.createElement("sl-icon");
        iconSave.name = "box-arrow-in-down";
        iconSave.slot = "prefix";

        this._saveButton = document.createElement("sl-button");
        this._saveButton.innerHTML = this.labels.saveChange;
        this._saveButton.variant = "primary";
        this._saveButton.appendChild(iconSave);
        this._saveButton.addEventListener("click", this.changeNameInDB);

        const icon = document.createElement("sl-icon");
        icon.name = "trash";
        icon.label = this.labels.deleteFilter;
        icon.slot = "prefix";

        this._deleteButton = document.createElement("sl-button");
        this._deleteButton.innerHTML = this.labels.deleteFilter;
        this._deleteButton.variant = "danger";
        this._deleteButton.appendChild(icon);
        this._deleteButton.addEventListener("click", this.removeFIlterDB);

        const spacer = document.createElement("div");
        spacer.className = "spacer";

        this._divEdit.appendChild(this._editNameInput);
        this._divEdit.appendChild(this._saveButton);
        this._divEdit.appendChild(spacer);
        this._divEdit.appendChild(this._deleteButton);
      } else {
        this._editNameInput.classList.remove("has-error");
        this._editNameInput.value = selectedFilter.name;
        this._editNameInput.placeholder = selectedFilter.name;
      }

      removeChildren(this._showConditionsHtml);
      const filterTree = new FilterTree<TData>({
        labels: this.labels,
        filterTree: selectedFilter.filterDefinition,
        fieldsByKey: this.fieldsByKey,
      });

      const filterText = filterTree.toPrettyHtmlElement(
        (field) => this.fieldsByKey.get(field)?.label || String(field),
        (value, field) =>
          getFieldValueFromItem(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            { [field]: value } as any,
            field,
            this.fieldsByKey,
          ) as string,
        {
          inline: true,
        },
      );

      this._showConditionsHtml.appendChild(filterText);
    }
  }

  private changeNameInDB = () => {
    if (!this._editNameInput || !this.userOptionsStorage || !this._saveButton)
      return;

    // Handle name change
    const newName = this._editNameInput.value.trim();

    if (this.validateNameString(newName)) {
      const existingFilter = this.listOfSavedFilters.find(
        (filter) => filter.id === this.currentEditingFilterId,
      );

      if (existingFilter) {
        this._saveButton.disabled = true;
        this._saveButton.loading = true;

        this.userOptionsStorage
          .storeFilterTree(
            existingFilter.filterDefinition,
            existingFilter.id,
            String(newName),
            existingFilter.scope,
          )
          .then(async () => {
            await this._updateListOfFiltersInRadioGroup();
            this._radioButtonsList.value = existingFilter.id;
            this.onChangeFilter(this.listOfSavedFilters);
          })
          .finally(() => {
            if (!this._saveButton) return;
            this._saveButton.disabled = false;
            this._saveButton.loading = false;
          });
      }
    } else {
      this.validateNameUI();
    }
  };

  private validateNameString = (newName: string | undefined) => {
    if (!newName) return false;
    if (newName.length < 3) return false;
    if (newName.length > 50) return false;

    newName = newName.trim();
    if (!newName) return false;

    return this.listOfSavedFilters.every(
      (filter) =>
        filter.name.toUpperCase() !== newName.toUpperCase() ||
        filter.id === this.currentEditingFilterId,
    );
  };

  private validateNameUI = () => {
    if (!this._editNameInput || !this._saveButton) return;

    const isUnique = this.validateNameString(this._editNameInput.value);

    if (!isUnique) {
      this._editNameInput.classList.add("has-error");
      this._saveButton.disabled = true;
    } else {
      this._editNameInput.classList.remove("has-error");
      this._saveButton.disabled = false;
    }
  };

  private removeFIlterDB = () => {
    if (!this.userOptionsStorage || !this._deleteButton) return;
    this._deleteButton.disabled = true;
    this._deleteButton.loading = true;
    const selectedFilter = this.listOfSavedFilters.find(
      (filter) => filter.id === this.currentEditingFilterId,
    );
    if (selectedFilter) {
      this.userOptionsStorage
        .removeFilterTree(selectedFilter.id)
        .then(async () => {
          await this._updateListOfFiltersInRadioGroup();
          this._radioButtonsList.value = "";
          this.currentEditingFilterId = undefined;
          this.onChangeFilter(this.listOfSavedFilters);
        })
        .finally(() => {
          if (!this._deleteButton) return;
          this._deleteButton.disabled = false;
          this._deleteButton.loading = false;
        });
    }
  };

  private _updateListOfFiltersInRadioGroup = async () => {
    const newList = await this.userOptionsStorage?.fetchAllFilterTrees();

    if (newList) {
      this.listOfSavedFilters = newList.sort((a, b) =>
        a.name.localeCompare(b.name),
      );

      removeChildren(this._radioButtonsList);
      this.listOfSavedFilters.forEach((filter) => {
        const radioButton = document.createElement("sl-radio");
        radioButton.value = filter.id;
        radioButton.innerHTML = filter.name;
        this._radioButtonsList.appendChild(radioButton);
      });
    }
  };
}
